import { useState, useCallback, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
// Dynamic import: PDF libraries loaded only when exporting
// import { pdf } from '@react-pdf/renderer'
import { useAuthedApi } from '../../hooks/useAuthedApi'
import { EstimatePdf } from './EstimatePdf'
import type { PdfLineItem } from './EstimatePdf'

// ── Types ─────────────────────────────────────────────────────────────────────

interface LineItem {
  markId: string
  tagLabel: string | null
  description: string
  componentCategory: string
  componentSubcategory: string
  quantity: number
  unit: string
  laborHrsPerUnit: number
  materialCostUsd: number
  directLaborUsd: number
  directMaterialUsd: number
  totalDirectUsd: number
}

interface LineItemsResponse {
  grouped: Record<string, LineItem[]>
  items: LineItem[]
  summary: {
    totalDirectLaborUsd: number
    totalDirectMaterialUsd: number
    totalDirectCostUsd: number
    markCount: number
  }
}

interface EstimateConfig {
  exists: boolean
  estimateNumber?: string
  laborBurdenPct: number
  overheadPct: number
  profitPct: number
  additionalWorkUsd?: number
  subcontractorUsd?: number
  equipmentSupplierUsd?: number
  defaults?: { laborBurdenPct: number; overheadPct: number; profitPct: number }
}

interface ProjectBasic {
  id: string
  name: string
  projectNumber: string | null
}

interface LaborRate {
  id: string
  trade: string
  description: string
  baseRateUsd: number
  isSystemDefault: boolean
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function usd(n: number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
}

function unitLabel(u: string) {
  if (u === 'SQ_FT') return 'sf'
  return u.toLowerCase()
}

const CATEGORY_ORDER = ['Ductwork', 'Equipment', 'Piping', 'Controls']

const CATEGORY_STYLE: Record<string, { bg: string; accent: string; light: string }> = {
  Ductwork:  { bg: '#EBF4FB', accent: '#1D6FAE', light: '#DBEAFE' },
  Piping:    { bg: '#E0F7FA', accent: '#0E7490', light: '#CFFAFE' },
  Equipment: { bg: '#FEF0E7', accent: '#C2460A', light: '#FED7AA' },
  Controls:  { bg: '#F3EFFE', accent: '#6D3FA8', light: '#E9D5FF' },
}

function catStyle(cat: string) {
  return CATEGORY_STYLE[cat] ?? { bg: '#F3F0EB', accent: '#5C5550', light: '#E2D9CC' }
}

// ── Column header ─────────────────────────────────────────────────────────────

function TH({ children, align = 'left', w }: { children: React.ReactNode; align?: string; w?: number | string }) {
  return (
    <th
      style={{
        padding: '8px 10px',
        textAlign: align as React.CSSProperties['textAlign'],
        fontSize: 11,
        fontWeight: 700,
        color: '#FDFAF5',
        whiteSpace: 'nowrap',
        width: w,
      }}
    >
      {children}
    </th>
  )
}

// ── Totals row ────────────────────────────────────────────────────────────────

function TotalsRow({
  label,
  value,
  sub,
  grand,
}: {
  label: string
  value: string
  sub?: boolean
  grand?: boolean
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: grand ? '10px 16px' : '7px 16px',
        background: grand ? '#1B5E3B' : sub ? '#F8F4EE' : '#fff',
        borderBottom: grand ? 'none' : '1px solid #E2D9CC',
      }}
    >
      <span
        style={{
          fontSize: grand ? 13 : 12,
          fontWeight: grand ? 700 : sub ? 400 : 400,
          color: grand ? '#fff' : sub ? '#5C5550' : '#1A1612',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: grand ? 14 : 12,
          fontWeight: 700,
          color: grand ? '#fff' : '#1A1612',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </span>
    </div>
  )
}

// ── PctInput ──────────────────────────────────────────────────────────────────

function PctInput({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#5C5550', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label} %
      </label>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #E2D9CC', borderRadius: 6, overflow: 'hidden', background: '#FDFAF5' }}>
        <input
          type="number"
          min={0}
          max={100}
          step={1}
          value={Math.round(value * 100)}
          onChange={(e) => onChange(Math.max(0, Math.min(100, Number(e.target.value))) / 100)}
          style={{
            width: 60,
            padding: '7px 8px',
            border: 'none',
            background: 'transparent',
            fontSize: 13,
            color: '#1A1612',
            outline: 'none',
            textAlign: 'right',
          }}
        />
        <span style={{ paddingRight: 8, fontSize: 13, color: '#5C5550' }}>%</span>
      </div>
    </div>
  )
}

// ── UsdInput ──────────────────────────────────────────────────────────────────

function UsdInput({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <label style={{ fontSize: 11, fontWeight: 700, color: '#5C5550', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </label>
      <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #E2D9CC', borderRadius: 6, overflow: 'hidden', background: '#FDFAF5' }}>
        <span style={{ paddingLeft: 8, fontSize: 13, color: '#5C5550' }}>$</span>
        <input
          type="number"
          min={0}
          step={100}
          value={value}
          onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
          style={{
            flex: 1,
            padding: '7px 8px',
            border: 'none',
            background: 'transparent',
            fontSize: 13,
            color: '#1A1612',
            outline: 'none',
            textAlign: 'right',
          }}
        />
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function EstimatePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { get, put, post } = useAuthedApi()
  const queryClient = useQueryClient()
  const [exporting, setExporting] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [rematching, setRematching] = useState(false)
  // Local editable markup state
  const [localLaborRateId, setLocalLaborRateId] = useState<string | null>(null)
  const [localBurden, setLocalBurden] = useState<number | null>(null)
  const [localOverhead, setLocalOverhead] = useState<number | null>(null)
  const [localProfit, setLocalProfit] = useState<number | null>(null)
  const [localAdditionalWork, setLocalAdditionalWork] = useState<number | null>(null)
  const [localSubcontractor, setLocalSubcontractor] = useState<number | null>(null)
  const [localEquipmentSupplier, setLocalEquipmentSupplier] = useState<number | null>(null)

  // ── Fetch project basics ───────────────────────────────────────────────────
  const { data: project } = useQuery<ProjectBasic>({
    queryKey: ['project-basic', id],
    queryFn: () => get(`/projects/${id}`),
    enabled: !!id,
    staleTime: 60_000,
  })

  // ── Fetch estimate config (markup %) ──────────────────────────────────────
  const { data: estimateRaw } = useQuery<EstimateConfig>({
    queryKey: ['estimate-config', id],
    queryFn: async () => {
      const raw = await get<Record<string, unknown>>(`/projects/${id}/estimate`)
      // Normalise: exists=false ships defaults nested, exists=true ships flat
      if (raw.exists === false) {
        const d = raw.defaults as EstimateConfig
        return { exists: false, laborBurdenPct: d.laborBurdenPct, overheadPct: d.overheadPct, profitPct: d.profitPct }
      }
      return raw as unknown as EstimateConfig
    },
    enabled: !!id,
    staleTime: 60_000,
  })

  // ── Fetch labor rates ──────────────────────────────────────────────────────
  const { data: laborRates } = useQuery<LaborRate[]>({
    queryKey: ['labor-rates'],
    queryFn: () => get('/pricing/labor-rates'),
    staleTime: 300_000,
  })

  // ── Fetch line items ───────────────────────────────────────────────────────
  const { data: lineItemsData, isLoading, isError } = useQuery<LineItemsResponse>({
    queryKey: ['estimate-items', id],
    queryFn: () => get(`/projects/${id}/estimate/line-items`),
    enabled: !!id,
    staleTime: 30_000,
  })

  // Sync local config state when data arrives (only first load)
  useEffect(() => {
    if (!estimateRaw) return
    if (localBurden === null) setLocalBurden(estimateRaw.laborBurdenPct)
    if (localOverhead === null) setLocalOverhead(estimateRaw.overheadPct)
    if (localProfit === null) setLocalProfit(estimateRaw.profitPct)
    if (localAdditionalWork === null) setLocalAdditionalWork(estimateRaw.additionalWorkUsd ?? 0)
    if (localSubcontractor === null) setLocalSubcontractor(estimateRaw.subcontractorUsd ?? 0)
    if (localEquipmentSupplier === null) setLocalEquipmentSupplier(estimateRaw.equipmentSupplierUsd ?? 0)
  }, [estimateRaw]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (laborRates && !localLaborRateId) {
      const first = laborRates.find((r) => r.isSystemDefault) ?? laborRates[0]
      if (first) setLocalLaborRateId(first.id)
    }
  }, [laborRates]) // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save estimate config ───────────────────────────────────────────────────
  const handleSaveConfig = useCallback(async () => {
    if (!localLaborRateId || saving) return
    setSaving(true)
    try {
      await put(`/projects/${id}/estimate`, {
        laborRateId:        localLaborRateId,
        laborBurdenPct:     localBurden   ?? 0.22,
        overheadPct:        localOverhead ?? 0.12,
        profitPct:          localProfit   ?? 0.15,
        additionalWorkUsd:  localAdditionalWork ?? 0,
        subcontractorUsd:   localSubcontractor ?? 0,
        equipmentSupplierUsd: localEquipmentSupplier ?? 0,
      })
      await queryClient.invalidateQueries({ queryKey: ['estimate-config', id] })
      await queryClient.invalidateQueries({ queryKey: ['estimate-items', id] })
      setConfigOpen(false)
    } catch (e) {
      console.error('Save config failed', e)
    } finally {
      setSaving(false)
    }
  }, [id, localLaborRateId, localBurden, localOverhead, localProfit, localAdditionalWork, localSubcontractor, localEquipmentSupplier, saving, put, queryClient])

  // ── Rematch pricing ────────────────────────────────────────────────────────
  const handleRematch = useCallback(async () => {
    if (rematching) return
    setRematching(true)
    try {
      await post(`/projects/${id}/estimate/rematch-pricing`, {})
      await queryClient.invalidateQueries({ queryKey: ['estimate-items', id] })
    } catch (e) {
      console.error('Rematch failed', e)
    } finally {
      setRematching(false)
    }
  }, [id, rematching, post, queryClient])

  // ── Derived numbers ────────────────────────────────────────────────────────
  const laborBurdenPct = localBurden  ?? estimateRaw?.laborBurdenPct ?? 0.22
  const overheadPct    = localOverhead ?? estimateRaw?.overheadPct    ?? 0.12
  const profitPct      = localProfit   ?? estimateRaw?.profitPct      ?? 0.15

  const directLabor    = lineItemsData?.summary.totalDirectLaborUsd    ?? 0
  const directMaterial = lineItemsData?.summary.totalDirectMaterialUsd ?? 0
  const directCost     = lineItemsData?.summary.totalDirectCostUsd     ?? 0
  const laborBurden    = directLabor * laborBurdenPct
  const overhead       = directCost  * overheadPct
  const profit         = directCost  * profitPct
  const additionalWork = localAdditionalWork ?? estimateRaw?.additionalWorkUsd ?? 0
  const subcontractor  = localSubcontractor ?? estimateRaw?.subcontractorUsd ?? 0
  const equipmentSupplier = localEquipmentSupplier ?? estimateRaw?.equipmentSupplierUsd ?? 0
  const grandTotal     = directCost  + laborBurden + overhead + profit + additionalWork + subcontractor + equipmentSupplier

  // Category ordering
  const grouped = lineItemsData?.grouped ?? {}
  const orderedCategories = [
    ...CATEGORY_ORDER.filter((c) => grouped[c]),
    ...Object.keys(grouped).filter((c) => !CATEGORY_ORDER.includes(c)),
  ]

  // ── PDF export ─────────────────────────────────────────────────────────────
  const handleExportPdf = useCallback(async () => {
    if (!lineItemsData || exporting) return
    setExporting(true)
    try {
      // Dynamically import PDF library only when needed
      const { pdf } = await import('@react-pdf/renderer')

      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
      const estimateNumber = estimateRaw?.estimateNumber ?? `EP-${new Date().getFullYear()}-DRAFT`

      // Coerce to PdfLineItem[]
      const pdfGrouped: Record<string, PdfLineItem[]> = {}
      for (const [cat, items] of Object.entries(grouped)) {
        pdfGrouped[cat] = items.map((i) => ({ ...i }))
      }

      const blob = await pdf(
        <EstimatePdf
          data={{
            projectName:       project?.name ?? 'Project',
            estimateNumber,
            date:              today,
            grouped:           pdfGrouped,
            directLaborUsd:    directLabor,
            directMaterialUsd: directMaterial,
            directCostUsd:     directCost,
            laborBurdenPct,
            laborBurdenUsd:    laborBurden,
            overheadPct,
            overheadUsd:       overhead,
            profitPct,
            profitUsd:         profit,
            additionalWorkUsd: additionalWork,
            subcontractorUsd: subcontractor,
            equipmentSupplierUsd: equipmentSupplier,
            grandTotal,
          }}
        />
      ).toBlob()

      const url = URL.createObjectURL(blob)
      const a   = document.createElement('a')
      a.href    = url
      a.download = `${estimateNumber}.pdf`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('PDF export failed', e)
    } finally {
      setExporting(false)
    }
  }, [lineItemsData, exporting, project, estimateRaw, grouped, directLabor, directMaterial, directCost, laborBurdenPct, laborBurden, overheadPct, overhead, profitPct, profit, grandTotal])

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: 'calc(100vh - var(--nav-height, 56px))',
        background: '#FDFAF5',
        paddingBottom: 60,
      }}
    >
      {/* ── Top bar ── */}
      <div
        style={{
          background: '#fff',
          borderBottom: '1px solid #E2D9CC',
          padding: '0 24px',
          height: 52,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate(`/projects/${id}`)}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: 13,
            color: '#5C5550',
            padding: '4px 8px',
            borderRadius: 5,
            display: 'flex',
            alignItems: 'center',
            gap: 5,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = '#1A1612')}
          onMouseLeave={(e) => (e.currentTarget.style.color = '#5C5550')}
        >
          ← {project?.name ?? 'Project'}
        </button>

        <div style={{ width: 1, height: 18, background: '#E2D9CC' }} />

        <span style={{ fontSize: 13, fontWeight: 700, color: '#1A1612', flex: 1 }}>
          Estimate
          {estimateRaw?.estimateNumber && (
            <span style={{ fontWeight: 400, color: '#5C5550', marginLeft: 8 }}>
              {estimateRaw.estimateNumber}
            </span>
          )}
        </span>

        <button
          onClick={handleRematch}
          disabled={rematching}
          title="Re-match pricing rates for all confirmed marks"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '10px 14px',
            background: 'transparent',
            color: rematching ? '#5C5550' : '#1A1612',
            border: '1px solid #E2D9CC',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: rematching ? 'not-allowed' : 'pointer',
            minHeight: 44,
          }}
          onMouseEnter={(e) => { if (!rematching) e.currentTarget.style.borderColor = '#1B5E3B' }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E2D9CC' }}
        >
          {rematching ? '↻ Matching…' : '↻ Rematch Pricing'}
        </button>

        <button
          onClick={() => setConfigOpen((v) => !v)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            padding: '10px 14px',
            background: configOpen ? '#F3F0EB' : 'transparent',
            color: '#1A1612',
            border: '1px solid #E2D9CC',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            minHeight: 44,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F0EB' }}
          onMouseLeave={(e) => { e.currentTarget.style.background = configOpen ? '#F3F0EB' : 'transparent' }}
        >
          ⚙ Settings
        </button>

        <button
          onClick={handleExportPdf}
          disabled={exporting || !lineItemsData}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            padding: '10px 16px',
            background: exporting || !lineItemsData ? '#E2D9CC' : '#1B5E3B',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            cursor: exporting || !lineItemsData ? 'not-allowed' : 'pointer',
            transition: 'background 0.15s',
            minHeight: 44,
          }}
          onMouseEnter={(e) => {
            if (!exporting && lineItemsData) e.currentTarget.style.background = '#256B47'
          }}
          onMouseLeave={(e) => {
            if (!exporting && lineItemsData) e.currentTarget.style.background = '#1B5E3B'
          }}
        >
          {exporting ? (
            <>
              <span
                style={{
                  width: 11,
                  height: 11,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: '#fff',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
              Exporting…
            </>
          ) : (
            '↓ Export PDF'
          )}
        </button>
      </div>

      {/* ── Config panel ── */}
      {configOpen && (
        <div
          style={{
            background: '#fff',
            borderBottom: '1px solid #E2D9CC',
            padding: '20px 24px',
          }}
        >
          <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            {/* Labor rate */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, minWidth: 200 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#5C5550', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Labor Rate
              </label>
              <select
                value={localLaborRateId ?? ''}
                onChange={(e) => setLocalLaborRateId(e.target.value)}
                style={{
                  padding: '7px 10px',
                  border: '1px solid #E2D9CC',
                  borderRadius: 6,
                  fontSize: 13,
                  background: '#FDFAF5',
                  color: '#1A1612',
                  cursor: 'pointer',
                }}
              >
                {(laborRates ?? []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.trade} — ${r.baseRateUsd.toFixed(2)}/hr
                  </option>
                ))}
              </select>
            </div>

            {/* Labor Burden % */}
            <PctInput
              label="Labor Burden"
              value={localBurden ?? 0.22}
              onChange={setLocalBurden}
            />

            {/* Overhead % */}
            <PctInput
              label="Overhead"
              value={localOverhead ?? 0.12}
              onChange={setLocalOverhead}
            />

            {/* Profit % */}
            <PctInput
              label="Profit"
              value={localProfit ?? 0.15}
              onChange={setLocalProfit}
            />

            {/* Additional Work $ */}
            <UsdInput
              label="Additional Work"
              value={localAdditionalWork ?? 0}
              onChange={setLocalAdditionalWork}
            />

            {/* Subcontractor $ */}
            <UsdInput
              label="Subcontractor"
              value={localSubcontractor ?? 0}
              onChange={setLocalSubcontractor}
            />

            {/* Equipment Supplier $ */}
            <UsdInput
              label="Equipment Supplier"
              value={localEquipmentSupplier ?? 0}
              onChange={setLocalEquipmentSupplier}
            />

            {/* Save button */}
            <button
              onClick={handleSaveConfig}
              disabled={saving}
              style={{
                padding: '10px 20px',
                background: saving ? '#E2D9CC' : '#1B5E3B',
                color: '#fff',
                border: 'none',
                borderRadius: 6,
                fontSize: 13,
                fontWeight: 700,
                cursor: saving ? 'not-allowed' : 'pointer',
                minHeight: 44,
                alignSelf: 'flex-end',
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      )}

      {/* ── Content ── */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px 0' }}>

        {/* ── Loading ── */}
        {isLoading && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10, padding: '80px 0', color: '#5C5550', fontSize: 14 }}>
            <span style={{ width: 20, height: 20, border: '2px solid #E2D9CC', borderTopColor: '#1B5E3B', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
            Loading estimate…
          </div>
        )}

        {/* ── Error ── */}
        {isError && (
          <div style={{ textAlign: 'center', padding: '80px 0', color: '#991B1B', fontSize: 14 }}>
            Failed to load estimate data.
          </div>
        )}

        {/* ── Empty state ── */}
        {!isLoading && !isError && lineItemsData?.summary.markCount === 0 && (
          <div style={{ textAlign: 'center', padding: '80px 0' }}>
            <div style={{ fontSize: 36, marginBottom: 16 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#1A1612', marginBottom: 8 }}>No confirmed components</div>
            <div style={{ fontSize: 13, color: '#5C5550', marginBottom: 24 }}>
              Confirm components on the canvas to generate a cost estimate.
            </div>
            <button
              onClick={() => navigate(`/projects/${id}`)}
              style={{ padding: '8px 20px', background: '#1B5E3B', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              Back to Project
            </button>
          </div>
        )}

        {/* ── Zero-pricing warning ── */}
        {!isLoading && !isError && lineItemsData && (() => {
          const zeroPriced = lineItemsData.items.filter((i) => i.totalDirectUsd === 0)
          if (zeroPriced.length === 0) return null
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '12px 16px',
                background: '#FFFBEB',
                border: '1px solid #FDE68A',
                borderRadius: 8,
                marginBottom: 20,
              }}
            >
              <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E', marginBottom: 3 }}>
                  {zeroPriced.length} component{zeroPriced.length !== 1 ? 's' : ''} without pricing
                </div>
                <div style={{ fontSize: 12, color: '#B45309' }}>
                  These items show $0.00 because no pricing rate is assigned. Use{' '}
                  <strong>↻ Rematch Pricing</strong> to auto-assign rates, or edit each mark on the canvas.
                </div>
              </div>
            </div>
          )
        })()}

        {/* ── Main estimate table ── */}
        {!isLoading && !isError && lineItemsData && lineItemsData.summary.markCount > 0 && (
          <>
            {/* Line-items grouped by category */}
            <div
              style={{
                borderRadius: 8,
                overflow: 'hidden',
                border: '1px solid #E2D9CC',
                marginBottom: 24,
                boxShadow: '0 1px 4px rgba(26,22,18,0.06)',
              }}
            >
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#1A1612' }}>
                    <TH>Description</TH>
                    <TH align="right" w={56}>Qty</TH>
                    <TH align="center" w={52}>Unit</TH>
                    <TH align="right" w={72}>Labor Hrs</TH>
                    <TH align="right" w={100}>Material</TH>
                    <TH align="right" w={110}>Total</TH>
                  </tr>
                </thead>
                <tbody>
                  {orderedCategories.map((cat) => {
                    const items = grouped[cat] ?? []
                    const cs = catStyle(cat)
                    const catLabHrs = items.reduce((s, i) => s + i.laborHrsPerUnit * i.quantity, 0)
                    const catMat    = items.reduce((s, i) => s + i.directMaterialUsd, 0)
                    const catTotal  = items.reduce((s, i) => s + i.totalDirectUsd, 0)

                    return (
                      <>
                        {/* Category header row */}
                        <tr key={`${cat}-hdr`} style={{ background: cs.bg }}>
                          <td
                            colSpan={6}
                            style={{
                              padding: '8px 12px',
                              fontSize: 11,
                              fontWeight: 700,
                              color: cs.accent,
                              textTransform: 'uppercase',
                              letterSpacing: '0.05em',
                              borderBottom: `2px solid ${cs.accent}22`,
                            }}
                          >
                            {cat}
                            <span style={{ fontWeight: 400, marginLeft: 8, color: cs.accent + 'aa' }}>
                              ({items.length} item{items.length !== 1 ? 's' : ''})
                            </span>
                          </td>
                        </tr>

                        {/* Line items */}
                        {items.map((item, idx) => {
                          const isZero = item.totalDirectUsd === 0
                          return (
                          <tr
                            key={item.markId}
                            style={{ background: isZero ? '#FFFDF5' : idx % 2 === 0 ? '#fff' : '#FAF8F4', transition: 'background 0.1s' }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = isZero ? '#FFFBEB' : cs.bg)}
                            onMouseLeave={(e) => (e.currentTarget.style.background = isZero ? '#FFFDF5' : idx % 2 === 0 ? '#fff' : '#FAF8F4')}
                          >
                            <td style={{ padding: '9px 12px', borderBottom: '1px solid #F0EBE3' }}>
                              <div style={{ fontSize: 12, color: '#1A1612', lineHeight: 1.4, display: 'flex', alignItems: 'center', gap: 5 }}>
                                {isZero && <span title="No pricing rate assigned" style={{ fontSize: 11, color: '#B45309' }}>⚠</span>}
                                {item.description}
                              </div>
                              {item.tagLabel && (
                                <div style={{ fontSize: 10, color: '#5C5550', marginTop: 2 }}>
                                  <span style={{ background: '#EDE8E1', borderRadius: 3, padding: '1px 5px', fontFamily: 'monospace' }}>
                                    {item.tagLabel}
                                  </span>
                                </div>
                              )}
                            </td>
                            <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, fontWeight: 700, color: '#1A1612', borderBottom: '1px solid #F0EBE3', whiteSpace: 'nowrap' }}>
                              {item.quantity}
                            </td>
                            <td style={{ padding: '9px 12px', textAlign: 'center', fontSize: 11, color: '#5C5550', borderBottom: '1px solid #F0EBE3' }}>
                              {unitLabel(item.unit)}
                            </td>
                            <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: '#1A1612', borderBottom: '1px solid #F0EBE3', fontVariantNumeric: 'tabular-nums' }}>
                              {(item.laborHrsPerUnit * item.quantity).toFixed(1)}
                            </td>
                            <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, color: '#1A1612', borderBottom: '1px solid #F0EBE3', fontVariantNumeric: 'tabular-nums' }}>
                              {usd(item.directMaterialUsd)}
                            </td>
                            <td style={{ padding: '9px 12px', textAlign: 'right', fontSize: 12, fontWeight: 600, color: '#1A1612', borderBottom: '1px solid #F0EBE3', fontVariantNumeric: 'tabular-nums' }}>
                              {usd(item.totalDirectUsd)}
                            </td>
                          </tr>
                        )})}

                        {/* Category subtotal */}
                        <tr key={`${cat}-sub`} style={{ background: cs.light + '60' }}>
                          <td style={{ padding: '7px 12px', fontSize: 11, fontWeight: 700, color: cs.accent, borderBottom: `1px solid ${cs.accent}30`, borderTop: `1px solid ${cs.accent}30` }}>
                            {cat} Subtotal
                          </td>
                          <td style={{ borderBottom: `1px solid ${cs.accent}30`, borderTop: `1px solid ${cs.accent}30` }} />
                          <td style={{ borderBottom: `1px solid ${cs.accent}30`, borderTop: `1px solid ${cs.accent}30` }} />
                          <td style={{ padding: '7px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: cs.accent, borderBottom: `1px solid ${cs.accent}30`, borderTop: `1px solid ${cs.accent}30`, fontVariantNumeric: 'tabular-nums' }}>
                            {catLabHrs.toFixed(1)}
                          </td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: cs.accent, borderBottom: `1px solid ${cs.accent}30`, borderTop: `1px solid ${cs.accent}30`, fontVariantNumeric: 'tabular-nums' }}>
                            {usd(catMat)}
                          </td>
                          <td style={{ padding: '7px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: cs.accent, borderBottom: `1px solid ${cs.accent}30`, borderTop: `1px solid ${cs.accent}30`, fontVariantNumeric: 'tabular-nums' }}>
                            {usd(catTotal)}
                          </td>
                        </tr>
                      </>
                    )
                  })}
                </tbody>
              </table>
            </div>

            {/* ── Totals section ── */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginBottom: 40,
              }}
            >
              <div
                style={{
                  width: 320,
                  border: '1px solid #E2D9CC',
                  borderRadius: 8,
                  overflow: 'hidden',
                  boxShadow: '0 1px 4px rgba(26,22,18,0.06)',
                }}
              >
                <TotalsRow label="Direct Labor"                              value={usd(directLabor)} />
                <TotalsRow label="Direct Material"                           value={usd(directMaterial)} />
                <TotalsRow label="Direct Cost"                               value={usd(directCost)} />
                <TotalsRow label={`Labor Burden (${Math.round(laborBurdenPct * 100)}%)`} value={usd(laborBurden)} sub />
                <TotalsRow label={`Overhead (${Math.round(overheadPct * 100)}%)`}         value={usd(overhead)} sub />
                <TotalsRow label={`Profit (${Math.round(profitPct * 100)}%)`}             value={usd(profit)} sub />
                {additionalWork > 0 && <TotalsRow label="Additional Work"                 value={usd(additionalWork)} sub />}
                {subcontractor > 0 && <TotalsRow label="Subcontractor"                    value={usd(subcontractor)} sub />}
                {equipmentSupplier > 0 && <TotalsRow label="Equipment Supplier"           value={usd(equipmentSupplier)} sub />}
                <TotalsRow label="Grand Total"                               value={usd(grandTotal)} grand />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
